import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { EmployerLearnerFilter } from '../shared/interfaces/employerLearnerFilter';
import { EmployerContact } from '../shared/interfaces/employer-contacts';
import { Sort } from '@angular/material/sort';
import { EmployerEventFilter } from '../shared/interfaces/employer-event-filter';
import { EmployerEvent, EmployerEventFilterOptions, EmployerEvents } from '../shared/interfaces/employer-event';
import { EmployerHealthAndSafety } from '../shared/interfaces/homepage-event';
import {
  EmployerAttachment,
  EmployerBranchMaytasLinks,
  EmployerMaytasLink,
} from '../shared/interfaces/employer-responses';
import type { EmployerLearnerCounts, EmployerOverviewFilter } from './employer-overview/employer-overview.component';
import type {
  EmployerLearnerData,
  EmployerLeaversChart,
  EmployerOverviewData,
} from './employer-overview/employer-overview-interfaces';
import type { EmployerTemplateInfo } from './employer-template/employer-template-info';
import type { uploadResponse } from './employer-health-and-safety/health-and-safety-upload/health-and-safety-upload.component';
import { ApiHelper } from '../core/services/api-helper';

@Injectable({
  providedIn: 'root',
})
export class EmployerService extends ApiHelper {
  private readonly refreshAttachmentsSource = new BehaviorSubject<boolean>(false);

  fetchRefreshAttachments(): Observable<boolean> {
    return this.refreshAttachmentsSource.asObservable();
  }
  setRefreshAttachments(): void {
    this.refreshAttachmentsSource.next(true);
  }

  getTemplateInfo(rootId: number, branchId: number | null, staffId: number): Observable<EmployerTemplateInfo> {
    let params = new HttpParams().append('staffId', staffId.toString()).append('rootId', rootId.toString());

    if (branchId !== null) {
      params = params.append('branchId', branchId.toString());
    }

    return this.getWithAccessToken<EmployerTemplateInfo>(`${environment.API_URL}v2/Employer/TemplateInfo`, params);
  }

  getLearnerCounts(
    rootId: number,
    branchId: number | null,
    staffId: number,
    filterParams: EmployerOverviewFilter
  ): Observable<EmployerLearnerCounts> {
    let params = new HttpParams()
      .append('staffId', staffId.toString())
      .append('rootId', rootId.toString())
      .append('filters', JSON.stringify(filterParams));

    if (branchId !== null) {
      params = params.append('branchId', branchId.toString());
    }

    const url = `${environment.API_URL}v2/Employer/Learners/Counts`;
    return this.getWithAccessToken<EmployerLearnerCounts>(url, params);
  }

  getOverviewData(rootId: number, branchId: number | null, staffId: number): Observable<EmployerOverviewData> {
    let params = new HttpParams({}).append('staffId', staffId.toString()).append('rootId', rootId.toString());

    if (branchId !== null) {
      params = params.append('branchId', branchId.toString());
    }

    return this.getWithAccessToken<EmployerOverviewData>(`${environment.API_URL}v2/Employer/Overview`, params);
  }

  getLeaverChart(
    rootId: number,
    branchId: number | null,
    staffId: number,
    filterParams: EmployerOverviewFilter
  ): Observable<EmployerLeaversChart> {
    let params = new HttpParams()
      .append('rootId', rootId.toString())
      .append('staffId', staffId.toString())
      .append('filters', JSON.stringify(filterParams));

    if (branchId !== null) {
      params = params.append('branchId', branchId.toString());
    }

    const url = `${environment.API_URL}v2/Employer/LeaverChart`;
    return this.getWithAccessToken<EmployerLeaversChart>(url, params);
  }

  getHealthAndSafetyData(rootId: number, staffId: number): Observable<EmployerHealthAndSafety> {
    return this.getWithAccessToken<EmployerHealthAndSafety>(
      `${environment.API_URL}v2/Employer/HealthAndSafety/${rootId}/${staffId}`
    );
  }

  getHealthAndSafetyAttachments(rootId: number, staffId: number): Observable<EmployerAttachment[]> {
    return this.getWithAccessToken<EmployerAttachment[]>(
      `${environment.API_URL}v2/Employer/HealthAndSafety/Attachments/${rootId}/${staffId}`
    );
  }

  getHealthAndSafetyAttachment(rootId: number, staffId: number, fileName: string): Observable<Blob> {
    return this.getFileWithAccessToken(
      `${environment.API_URL}v2/Employer/HealthAndSafety/Attachment/${rootId}/${staffId}/${fileName}`
    );
  }

  postHealthAndSafetyAttachment(rootId: number, staffId: number, files: FileList): Observable<uploadResponse> {
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      formData.append('files', file);
    });
    formData.append('employerId', rootId.toString());

    const url = `${environment.API_URL}v2/Employer/HealthAndSafety/Attachment/${rootId}/${staffId}`;
    return this.postFileWithAccessToken<FormData, uploadResponse>(url, formData);
  }

  getLearners(
    rootId: number,
    branchId: number | null,
    staffId: number,
    filters: EmployerLearnerFilter,
    pageNumber: number,
    pageSize: number,
    sort: Sort
  ): Observable<EmployerLearnerData> {
    const filterParam = {
      nameFilter: filters.nameFilter,
      sectorIds: filters.sectorIds.map((x) => x.id),
      activeOnly: filters.activeOnly,
      qualificationLevelIds: filters.qualificationLevelIds.map((x) => x.id),
      programmeTypeIds: filters.programmeTypeIds.map((x) => x.id),
    };

    const pagination = {
      skip: pageNumber * pageSize,
      take: pageSize,
      sortActive: sort.active,
      sortDirection: sort.direction,
    };

    let params = new HttpParams()
      .append('filters', JSON.stringify(filterParam))
      .append('staffId', staffId.toString())
      .append('pagination', JSON.stringify(pagination))
      .append('rootId', rootId.toString());

    if (branchId !== null) {
      params = params.append('branchId', branchId.toString());
    }

    const url = `${environment.API_URL}v2/Employer/Learners`;
    return this.getWithAccessToken<EmployerLearnerData>(url, params);
  }

  updateContact(contact: EmployerContact, staffId: number): Observable<EmployerContact> {
    return this.putWithAccessToken<{ contact: EmployerContact; staffId: number }, EmployerContact>(
      `${environment.API_URL}v2/Employer/Contact/${contact.id}`,
      { contact, staffId }
    );
  }

  updateHealthAndSafetyRiskRating(rootId: number, riskRatingId: number, staffId: number): Observable<boolean> {
    return this.postWithAccessToken<number, boolean>(
      `${environment.API_URL}v2/Employer/HealthAndSafety/RiskRating/${rootId}/${staffId}`,
      riskRatingId
    );
  }

  updateHealthAndSafetyEliDue(rootId: number, eliDue: Date, staffId: number): Observable<string> {
    return this.postWithAccessToken<Date, string>(
      `${environment.API_URL}v2/Employer/HealthAndSafety/Eli/${rootId}/${staffId}`,
      eliDue
    );
  }

  createContact(
    rootId: number,
    branchId: number,
    contact: EmployerContact,
    staffId: number
  ): Observable<EmployerContact> {
    return this.postWithAccessToken<
      { contact: EmployerContact; staffId: number; branchId: number; rootId: number },
      EmployerContact
    >(`${environment.API_URL}v2/Employer/Contact/`, {
      contact,
      staffId,
      branchId,
      rootId,
    });
  }

  deleteContact(contactId: number, staffId: number): Observable<void> {
    const params = new HttpParams().set('staffId', staffId.toString());
    return this.deleteWithAccessToken<void>(`${environment.API_URL}v2/Employer/Contact/${contactId}`, params);
  }

  getEvent(eventId: number, staffId: number): Observable<EmployerEvent> {
    const params = new HttpParams().set('staffId', JSON.stringify(staffId));

    return this.getWithAccessToken<EmployerEvent>(`${environment.API_URL}v2/Employer/Event/${eventId}`, params);
  }

  getEvents(
    rootId: number,
    branchId: number | null,
    staffId: number,
    filters: EmployerEventFilter,
    pageNumber: number,
    pageSize: number
  ): Observable<EmployerEvents> {
    const pagination = {
      skip: pageNumber * pageSize,
      take: pageSize,
    };

    let params = new HttpParams()
      .append('filters', JSON.stringify(filters))
      .append('pagination', JSON.stringify(pagination))
      .append('staffId', staffId.toString())
      .append('rootId', rootId.toString());

    if (branchId !== null) {
      params = params.append('branchId', branchId.toString());
    }

    const url = `${environment.API_URL}v2/Employer/Events`;
    return this.getWithAccessToken<EmployerEvents>(url, params);
  }

  getEventFilterOptions(rootId: number, staffId: number): Observable<EmployerEventFilterOptions> {
    return this.getWithAccessToken<EmployerEventFilterOptions>(
      `${environment.API_URL}v2/Employer/Events/FilterOptions/${rootId}`,
      new HttpParams().set('staffId', staffId.toString())
    );
  }

  deleteEvent(eventId: number, staffId: number): Observable<void> {
    return this.deleteWithAccessToken<void>(
      `${environment.API_URL}v2/Employer/Event/${eventId}`,
      new HttpParams().set('staffId', staffId.toString())
    );
  }

  updateEvent(
    event: EmployerEvent,
    staffId: number,
    rootId: number,
    branchId: number | null
  ): Observable<EmployerEvent> {
    return this.putWithAccessToken<
      { event: EmployerEvent; staffId: number; branchId: number | null; rootId: number },
      EmployerEvent
    >(`${environment.API_URL}v2/Employer/Event/${event.id}`, {
      event,
      staffId,
      branchId,
      rootId,
    });
  }

  createEvent(
    rootId: number,
    branchId: number | null,
    event: EmployerEvent,
    staffId: number
  ): Observable<EmployerEvent> {
    return this.postWithAccessToken<
      { event: EmployerEvent; staffId: number; rootId: number; branchId: number | null },
      EmployerEvent
    >(`${environment.API_URL}v2/Employer/Event/`, { event, staffId, rootId, branchId });
  }

  getMaytasLinks(rootId: number, branchId: number, staffId: number): Observable<EmployerBranchMaytasLinks[]> {
    let params = new HttpParams().append('staffId', staffId.toString());

    if (branchId !== null) {
      params = params.append('branchId', branchId.toString());
    }

    return this.getWithAccessToken<EmployerBranchMaytasLinks[]>(
      `${environment.API_URL}v2/Employer/MaytasLinks/${rootId}`,
      params
    );
  }

  removeMaytasLink(branchId: number, maytasEmployerId: string, staffId: number): Observable<void> {
    return this.deleteWithAccessToken<void>(
      `${environment.API_URL}v2/Employer/MaytasLinks/${branchId}/${maytasEmployerId}`,
      new HttpParams().set('staffId', staffId.toString())
    );
  }

  getMaytasLinksAvailable(): Observable<EmployerMaytasLink[]> {
    return this.getWithAccessToken<EmployerMaytasLink[]>(`${environment.API_URL}v2/Employer/MaytasLinks/Available`);
  }

  postMaytasLink(branchId: number, maytasEmployerId: string, staffId: number): Observable<EmployerBranchMaytasLinks> {
    return this.postWithAccessToken<number, EmployerBranchMaytasLinks>(
      `${environment.API_URL}v2/Employer/MaytasLinks/${branchId}/${maytasEmployerId}`,
      staffId
    );
  }
}
